/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { RequestInterceptor, ResponseInterceptor, FetchClientConfig } from './types'

export class FetchError extends Error {
  constructor(public status: number, public statusText: string, message: string) {
    super(message)
    this.name = 'FetchError'
  }
}

class FetchClient {
  private baseURL: string
  private requestInterceptors: RequestInterceptor[] = []
  private responseInterceptors: ResponseInterceptor[] = []
  private defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json'
  }

  constructor(config: FetchClientConfig = {}) {
    this.baseURL = config.baseURL || ''
  }

  private async executeRequest(
    url: string,
    options: RequestInit & { params?: Record<string, any> }
  ): Promise<Response> {
    let finalUrl = url.startsWith('http') ? url : this.baseURL + url
    const { params, ...fetchOptions } = options

    // Add query parameters
    if (params) {
      const queryString = new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([k, v]) => [k, String(v)])
      ).toString()
      if (queryString) {
        finalUrl += (finalUrl.includes('?') ? '&' : '?') + queryString
      }
    }

    // Set default headers
    const headers = {
      ...this.defaultHeaders,
      ...fetchOptions.headers
    }

    // Create request config
    let request = { ...fetchOptions, headers, url: finalUrl, method: fetchOptions.method || 'GET' }

    // Apply request interceptors
    for (const interceptor of this.requestInterceptors) {
      request = await interceptor(request as any)
    }

    // Extract signal before passing to fetch (in case an interceptor added it)
    const { url: requestUrl, ...fetchConfig } = request

    const response = await fetch(requestUrl, fetchConfig)

    // Apply response interceptors
    let processedResponse = response
    for (const interceptor of this.responseInterceptors) {
      processedResponse = await interceptor(processedResponse)
    }

    return processedResponse
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = new FetchError(
        response.status,
        response.status.toString(),
        `HTTP error! status: ${response.status}`
      )
      throw error
    }
    return response.json()
  }

  async get<T = any>(url: string, config?: any): Promise<T> {
    const response = await this.executeRequest(url, {
      ...config,
      method: 'GET'
    })
    return this.handleResponse<T>(response)
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const requestConfig = {
      ...config,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    }

    const response = await this.executeRequest(url, requestConfig)
    return this.handleResponse<T>(response)
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const requestConfig = {
      ...config,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    }

    const response = await this.executeRequest(url, requestConfig)
    return this.handleResponse<T>(response)
  }

  async delete<T = any>(url: string, config?: any): Promise<T> {
    const response = await this.executeRequest(url, {
      ...config,
      method: 'DELETE'
    })
    return this.handleResponse<T>(response)
  }

  async patch<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const requestConfig = {
      ...config,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    }

    const response = await this.executeRequest(url, requestConfig)
    return this.handleResponse<T>(response)
  }

  setHeader(key: string, value: string): void {
    this.defaultHeaders[key] = value
  }

  removeHeader(key: string): void {
    delete this.defaultHeaders[key]
  }

  getInterceptors() {
    return {
      request: {
        use: (interceptor: RequestInterceptor) => {
          this.requestInterceptors.push(interceptor)
        }
      },
      response: {
        use: (interceptor: ResponseInterceptor) => {
          this.responseInterceptors.push(interceptor)
        }
      }
    }
  }
}

const httpClient = new FetchClient({
  baseURL: process.env.REACT_APP_API_URL
})

export default httpClient
